import React, { useState } from 'react';
import { Instagram, Check, X } from 'lucide-react';
import logoFlat from './logo-flat.png';
import bgImage from './landing-bg.png';
import vector86 from './Vector 86.png';
import vector85 from './Vector 85.png';
import { formatPhone } from './utils/phone';

const socialButtonStyle = {
  background: '#4A3E2B',
  boxShadow: '4px 4px 12px rgba(141, 126, 105, 0.1), inset 2px 2px 2px rgba(236, 231, 225, 0.3), inset 4px 4px 12px rgba(141, 126, 105, 0.2), inset -2px -2px 2px rgba(141, 126, 105, 0.2)',
  WebkitTapHighlightColor: 'transparent',
};

export default function App() {
  const [phone, setPhone] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSmsTerms, setShowSmsTerms] = useState(false);
  const [closingPrivacy, setClosingPrivacy] = useState(false);
  const [closingSmsTerms, setClosingSmsTerms] = useState(false);

  const closePrivacy = () => {
    setClosingPrivacy(true);
    setTimeout(() => { setShowPrivacy(false); setClosingPrivacy(false); }, 200);
  };

  const closeSmsTerms = () => {
    setClosingSmsTerms(true);
    setTimeout(() => { setShowSmsTerms(false); setClosingSmsTerms(false); }, 200);
  };

  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, '');
    let countryCode = false;
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
      countryCode = true;
    }
    digits = digits.slice(0, 10);
    setPhoneDigits(digits);
    setPhone(formatPhone(digits, { showCountryCode: countryCode }));
  };

  const handleSubmit = async () => {
    if (phoneDigits.length !== 10) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: phoneDigits }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to join waitlist');
      }

      const data = await response.json();
      if (data.success) {
        setSubmitted(data.message || "You're on the list!");
        setTimeout(() => {
          setPhone('');
          setPhoneDigits('');
          setSubmitted(false);
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', color: '#F5EDE2' }}>
      {/* Background Image */}
      <div className="fixed inset-0 z-0" style={{ background: '#4A3E2B' }}>
        <img
          src={bgImage}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Blur gradient layers */}
      <div className="fixed z-[1] pointer-events-none" style={{ width: '859px', height: '556px', left: '50%', top: '-163px', transform: 'translateX(-50%)', background: 'rgba(0, 0, 0, 0.05)', filter: 'blur(123px)' }} />
      <div className="fixed z-[1] pointer-events-none" style={{ width: '1387px', height: '946px', left: '50%', top: '277px', transform: 'translateX(-50%) rotate(180deg)', background: 'rgba(69, 43, 22, 0.1)', filter: 'blur(90px)' }} />

      {/* Header */}
      <header className="relative z-20 w-full px-6 sm:px-9 pt-[30px] sm:pt-[34px]">
        <div className="flex items-center justify-between">
          {/* Logo - left side */}
          <div className="flex items-center gap-2.5">
            <img
              src={logoFlat}
              alt="Palate"
              className="h-[36px] sm:h-[44px] object-contain"
            />
            <span className="font-spectral font-bold text-[18px] sm:text-[20px]" style={{ color: '#F5EDE2' }}>
              Palate
            </span>
          </div>

          {/* Social icons - right side */}
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/yourpalate_/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-[14px] flex items-center justify-center transition-opacity duration-200 hover:opacity-80"
              aria-label="Instagram"
              style={socialButtonStyle}
            >
              <Instagram className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#D5C9BB' }} />
            </a>
            <a
              href="https://substack.com/@yourpalate"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-[14px] flex items-center justify-center transition-opacity duration-200 hover:opacity-80"
              aria-label="Substack"
              style={socialButtonStyle}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="#D5C9BB" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Substack">
                <title>Substack</title>
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
              </svg>
            </a>
            <a
              href="https://x.com/yourpalate_"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-[14px] flex items-center justify-center transition-opacity duration-200 hover:opacity-80"
              aria-label="Twitter"
              style={socialButtonStyle}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="#D5C9BB" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="X">
                <title>X</title>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content - vertically centered */}
      <main className="relative z-20 flex-1 flex items-center justify-center px-4 sm:px-6 py-8">
        <div className="text-center w-full max-w-2xl mx-auto">
          {/* Headline - Spectral bold 44px */}
          <h1 className="font-spectral font-bold text-[32px] sm:text-[40px] md:text-[44px] leading-[1.16] mb-5 sm:mb-6" style={{ color: '#F5EDE2' }}>
            Picking dinner<br /> shouldn&rsquo;t be this hard
          </h1>

          {/* Subtitle - system font, medium weight, 20px */}
          <p className="text-[16px] sm:text-[18px] md:text-[20px] font-medium mb-7 sm:mb-8 max-w-xl mx-auto" style={{ color: '#F5EDE2' }}>
            Discover based on{' '}
            <span className="relative inline-block">
              your taste
              <img src={vector86} alt="" className="absolute -bottom-2 left-0 w-full pointer-events-none" />
            </span>
            {' & '}
            <span className="relative inline-block">
              your friends&rsquo;
              <img src={vector85} alt="" className="absolute -bottom-2 left-0 w-full pointer-events-none" />
            </span>
            {' recs'}
          </p>

          {/* Phone Input Form - pill shape with solid cream bg */}
          <div className="max-w-[380px] mx-auto mb-4 relative">
            {!submitted ? (
              <>
                <div
                  className="flex items-center rounded-full overflow-hidden"
                  style={{ background: '#F7F5F2', border: '1px solid rgba(236, 231, 225, 0.2)' }}
                >
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={loading}
                    className="flex-1 bg-transparent px-5 py-4 focus:outline-none text-sm disabled:opacity-50"
                    style={{ WebkitAppearance: 'none', fontSize: '16px', color: '#8D7E69' }}
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={phoneDigits.length !== 10 || loading}
                    className="rounded-full mr-1 shrink-0 font-semibold text-sm transition-opacity duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center min-h-[42px] min-w-[115px] px-4"
                    style={{
                      background: '#4A3E2B',
                      border: '1px solid rgba(236, 231, 225, 0.3)',
                      color: '#FBFAF9',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <span className="whitespace-nowrap">{loading ? 'Joining...' : 'Join waitlist'}</span>
                  </button>
                </div>
                {error && (
                  <div className="mt-3 bg-red-500/20 border border-red-500/30 rounded-full py-3 px-5 text-center">
                    <span className="text-red-300 font-light text-sm">{error}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-green-500/20 border border-green-500/30 rounded-full py-4 px-6 flex items-center justify-center gap-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-light">{submitted}</span>
              </div>
            )}
          </div>

          {/* SMS Terms - 16px, medium weight, 50% opacity */}
          <p className="text-base font-medium mt-3 mb-0 max-w-md mx-auto" style={{ color: '#F5EDE2', opacity: 0.5 }}>
            By joining, you agree to our{' '}
            <button
              onClick={() => setShowSmsTerms(true)}
              className="font-bold transition-opacity hover:opacity-80"
            >
              SMS Terms
            </button>
          </p>

          {/* App Store Button */}
          <a
            href="https://apps.apple.com/us/app/palate-discover-your-taste/id6758212217"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-5"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="h-[38px] mx-auto"
            />
          </a>
        </div>
      </main>

      {/* Footer - 16px system font */}
      <footer className="relative z-20 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-base" style={{ color: '#F5EDE2' }}>
          <span>Brooklyn, NY</span>
          <span className="w-[3px] h-[3px] rounded-full" style={{ background: '#F5EDE2' }} />
          <span>&copy; 2025 Palate</span>
          <span className="w-[3px] h-[3px] rounded-full" style={{ background: '#F5EDE2' }} />
          <a href="mailto:yourpalateapp@gmail.com" className="font-bold hover:opacity-80 transition-opacity">
            Contact us
          </a>
          <span className="w-[3px] h-[3px] rounded-full" style={{ background: '#F5EDE2' }} />
          <button
            onClick={() => setShowPrivacy(true)}
            className="font-bold hover:opacity-80 transition-opacity"
          >
            Privacy policy
          </button>
        </div>
      </footer>

      {/* SMS Terms Modal */}
      {showSmsTerms && (
        <div className={`modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4${closingSmsTerms ? ' closing' : ''}`} onClick={closeSmsTerms}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="modal-content relative rounded-[20px] max-w-lg w-full"
            style={{ background: '#4A3E2B' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4">
              <h2 className="text-lg font-spectral font-bold" style={{ color: '#F5EDE2' }}>SMS terms</h2>
              <button
                onClick={closeSmsTerms}
                className="transition-opacity hover:opacity-80"
                style={{ color: '#D5C9BB' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mx-6 sm:mx-8" style={{ borderTop: '1px solid rgba(213, 201, 187, 0.2)' }} />
            {/* Content */}
            <div className="px-6 sm:px-8 pt-5 pb-6 sm:pb-8">
              <p className="text-sm leading-relaxed" style={{ color: '#D5C9BB' }}>
                By joining the waitlist, you agree to a one-time SMS from Palate. Msg &amp; data rates may apply.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className={`modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4${closingPrivacy ? ' closing' : ''}`} onClick={closePrivacy}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="modal-content relative rounded-[20px] max-w-lg w-full max-h-[80vh] flex flex-col"
            style={{ background: '#4A3E2B' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4 shrink-0">
              <h2 className="text-lg font-spectral font-bold" style={{ color: '#F5EDE2' }}>Privacy policy</h2>
              <button
                onClick={closePrivacy}
                className="transition-opacity hover:opacity-80"
                style={{ color: '#D5C9BB' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mx-6 sm:mx-8 shrink-0" style={{ borderTop: '1px solid rgba(213, 201, 187, 0.2)' }} />
            {/* Scrollable content */}
            <div className="px-6 sm:px-8 pt-5 pb-6 sm:pb-8 overflow-y-auto text-sm leading-relaxed" style={{ color: '#D5C9BB' }}>
              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>What we collect</p>
              <p className="mb-5">When you join our waitlist, we collect your phone number. We do not collect any other personal information at this time.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>How we use it</p>
              <p className="mb-5">We use your phone number solely to send you a one-time SMS confirmation that you have been added to the Palate waitlist, and to notify you when Palate launches. We will not use your phone number for marketing or promotional messages beyond waitlist-related updates.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>No third-party sharing</p>
              <p className="mb-1">We do not sell, rent, or share your phone number with any third parties. Your data is stored securely and is only accessible to the Palate team.</p>
              <p className="mb-5">Data retention: We retain your phone number for the duration of the waitlist period. Once you opt out or the waitlist is no longer active, your data will be deleted.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>Opt out</p>
              <p className="mb-5">You can opt out at any time by replying REMOVE to any message you receive from us. Once you opt out, we will stop all messaging and remove your number from our active waitlist.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>Children&apos;s privacy</p>
              <p className="mb-5">Our service is not directed to individuals under 13. We do not knowingly collect personal information from children.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>Changes to this policy</p>
              <p className="mb-5">We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated date.</p>

              <p className="font-bold mb-1" style={{ color: '#F5EDE2' }}>Contact us</p>
              <p>If you have any questions about this Privacy Policy or your data, please contact us at <a href="mailto:yourpalateapp@gmail.com" className="underline hover:opacity-80 transition-opacity">yourpalateapp@gmail.com</a>.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
